/**
 * Vesta Marketplace Style Guide
 * 
 * This file contains all design system constants and component specifications
 * to ensure consistency across the application.
 * 
 * Last Updated: 2025-01-XX
 * Version: 1.1
 * 
 * NEW STANDARDS ADDED:
 * - FORM_ELEMENTS: Input fields, textarea, and read-only input specifications
 * - BUTTONS: Button sizing, padding, and typography standards
 * - VALIDATION: Error, success, and warning state specifications
 */

// ============================================================================
// COLOR PALETTE
// ============================================================================

export const COLORS = {
  // Primary Brand Colors - Vesta Gradient
  PRIMARY_GRADIENT: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
  PRIMARY_GRADIENT_START: '#f4a261',
  PRIMARY_GRADIENT_MIDDLE: '#e76f51',
  PRIMARY_GRADIENT_END: '#f4a261',
  
  // Legacy Primary Colors (kept for backward compatibility)
  PRIMARY_BROWN: '#f4a261', // Updated to match gradient start color
  PRIMARY_BROWN_DARK: '#e76f51', // Updated to match gradient middle color
  PRIMARY_BROWN_LIGHT: '#f4a261', // Updated to match gradient start color
  
  // Neutral Colors
  NEUTRAL_WHITE: '#FFF',
  NEUTRAL_BG: '#F9F9F9',
  NEUTRAL_TEXT: '#222',
  NEUTRAL_GRAY: '#E0E0E0',
  
  // Accent Colors
  ACCENT_GRAY: '#757575',
  ACCENT_BLUE: '#2563eb',
  ACCENT_BLUE_LIGHT: '#3b82f6',
  
  // Status Colors
  SUCCESS_GREEN: '#4CAF50',
  WARNING_ORANGE: '#FF9800',
  ERROR_RED: '#F44336',
  
  // Semantic Colors
  INFO_BLUE: '#2196F3',
  SUCCESS_GREEN_LIGHT: '#81C784',
  WARNING_ORANGE_LIGHT: '#FFB74D',
  ERROR_RED_LIGHT: '#E57373'
};

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const TYPOGRAPHY = {
  // Font Families
  FONT_FAMILY: {
    HEADING: 'Sora, sans-serif',
    BODY: 'Montserrat, sans-serif'
  },
  
  // Font Weights
  FONT_WEIGHT: {
    LIGHT: 300,
    REGULAR: 400,
    MEDIUM: 500,
    SEMIBOLD: 600,
    BOLD: 700,
    EXTRABOLD: 800
  },
  
  // Font Sizes
  FONT_SIZE: {
    XS: '12px',
    SM: '14px',
    BASE: '16px',
    LG: '18px',
    XL: '20px',
    '2XL': '24px',
    '3XL': '26px',
    '4XL': '32px'
  },
  
  // Onboarding-specific font sizes
  ONBOARDING: {
    MAIN_TITLE: '32px',        // "Simple as 1,2,3"
    H2: '28px',               // Secondary headings
    SUBTITLE: '14px',          // "Bring your delicious, wholesome food..."
    STEP_HEADING: '16px',      // "Tell us about your shop", "Open your shop", "Create your first product"
    STEP_DESCRIPTION: '14px',  // Description text of the steps
    BUTTON_TEXT: '14px',       // Button text
    STEP_NUMBER: '14px'        // Numbers in step circles
  },
  
  // Line Heights
  LINE_HEIGHT: {
    TIGHT: 1.25,
    NORMAL: 1.5,
    RELAXED: 1.75
  }
};

// ============================================================================
// FORM ELEMENTS & BUTTONS
// ============================================================================

export const FORM_ELEMENTS = {
  // Input Fields
  INPUT: {
    FONT_SIZE: '14px',
    PADDING: '12px 16px',
    BORDER_RADIUS: '8px',
    BACKGROUND: '#F9FAFB',
    BORDER: '1px solid #E5E7EB',
    BORDER_ERROR: '2px solid #F44336',
    TRANSITION: 'all 0.2s ease'
  },
  
  // Textarea
  TEXTAREA: {
    FONT_SIZE: '14px',
    PADDING: '12px 16px',
    BORDER_RADIUS: '8px',
    BACKGROUND: '#F9FAFB',
    BORDER: '1px solid #E5E7EB',
    MIN_HEIGHT: '100px',
    RESIZE: 'vertical'
  },
  
  // Read-only Input Fields
  INPUT_READONLY: {
    FONT_SIZE: '14px',
    PADDING: '12px 16px',
    BORDER_RADIUS: '8px',
    BACKGROUND: '#F3F4F6',
    BORDER: '1px solid #E5E7EB',
    COLOR: '#757575',
    CURSOR: 'not-allowed'
  }
};

export const BUTTONS = {
  // Primary Buttons
  PRIMARY: {
    FONT_SIZE: '14px',
    PADDING: '8px 16px',
    BORDER_RADIUS: '8px',
    FONT_WEIGHT: '500',
    TRANSITION: 'all 0.2s ease'
  },
  
  // Secondary Buttons
  SECONDARY: {
    FONT_SIZE: '14px',
    PADDING: '8px 16px',
    BORDER_RADIUS: '8px',
    FONT_WEIGHT: '500',
    TRANSITION: 'all 0.2s ease'
  },
  
  // Large Buttons (for CTAs)
  LARGE: {
    FONT_SIZE: '16px',
    PADDING: '12px 24px',
    BORDER_RADIUS: '8px',
    FONT_WEIGHT: '500',
    TRANSITION: 'all 0.2s ease'
  },
  
  // Small Buttons
  SMALL: {
    FONT_SIZE: '12px',
    PADDING: '6px 12px',
    BORDER_RADIUS: '6px',
    FONT_WEIGHT: '500',
    TRANSITION: 'all 0.2s ease'
  }
};

// ============================================================================
// VALIDATION & FEEDBACK
// ============================================================================

export const VALIDATION = {
  // Error States
  ERROR: {
    BORDER_COLOR: '#F44336',
    TEXT_COLOR: '#F44336',
    FONT_SIZE: '12px',
    MARGIN_TOP: '4px'
  },
  
  // Success States
  SUCCESS: {
    TEXT_COLOR: '#4CAF50',
    FONT_SIZE: '14px',
    FONT_WEIGHT: '500'
  },
  
  // Warning States
  WARNING: {
    TEXT_COLOR: '#FF9800',
    FONT_SIZE: '14px',
    FONT_WEIGHT: '500'
  }
};

// ============================================================================
// SPACING & LAYOUT
// ============================================================================

export const SPACING = {
  // Base spacing units (in pixels)
  XS: '4px',
  SM: '8px',
  MD: '16px',
  LG: '24px',
  XL: '32px',
  '2XL': '48px',
  '3XL': '64px',
  '4XL': '96px'
};

export const LAYOUT = {
  // Border radius
  BORDER_RADIUS: {
    SM: '6px',
    MD: '8px',
    LG: '10px',
    XL: '12px',
    '2XL': '16px',
    FULL: '50%'
  },
  
  // Shadows
  SHADOW: {
    SM: '0 1px 2px rgba(0, 0, 0, 0.05)',
    MD: '0 4px 6px rgba(0, 0, 0, 0.1)',
    LG: '0 10px 15px rgba(0, 0, 0, 0.1)',
    XL: '0 20px 25px rgba(0, 0, 0, 0.1)'
  },
  
  // Transitions
  TRANSITION: {
    FAST: '0.15s ease',
    NORMAL: '0.2s ease',
    SLOW: '0.3s ease'
  },
  
  // Gradient Animation
  GRADIENT_ANIMATION: {
    BACKGROUND_SIZE: '200% 200%',
    ANIMATION: 'gradientShift 3s ease infinite'
  }
};

// ============================================================================
// COMPONENT SPECIFICATIONS
// ============================================================================

export const COMPONENTS = {
  // ========================================================================
  // BACK BUTTON
  // ========================================================================
  BACK_BUTTON: {
    // Icon specifications
    ICON: {
      NAME: 'ChevronLeft',
      SET: 'regular', // 'regular', 'bold', 'bulk', 'broken', 'two-tone'
      SIZE: {
        SMALL: 16,    // For links and small buttons
        MEDIUM: 20,   // For standard buttons
        LARGE: 24     // For prominent buttons
      },
      COLOR: {
        PRIMARY: COLORS.PRIMARY_BROWN,
        SECONDARY: COLORS.NEUTRAL_TEXT,
        LINK: COLORS.ACCENT_BLUE,
        WHITE: COLORS.NEUTRAL_WHITE
      }
    },
    
    // Button styling
    BUTTON: {
      PADDING: {
        SMALL: '8px 12px',
        MEDIUM: '8px 16px',
        LARGE: '12px 20px'
      },
      BORDER_RADIUS: LAYOUT.BORDER_RADIUS.MD,
      BACKGROUND: 'transparent',
      BORDER: 'none',
      CURSOR: 'pointer',
      TRANSITION: LAYOUT.TRANSITION.NORMAL,
      
      // Hover states
      HOVER: {
        BACKGROUND: COLORS.NEUTRAL_BG,
        TRANSFORM: 'scale(1.02)'
      }
    },
    
    // Link styling (for navigation links)
    LINK: {
      DISPLAY: 'flex',
      ALIGN_ITEMS: 'center',
      GAP: SPACING.SM,
      TEXT_DECORATION: 'none',
      TRANSITION: LAYOUT.TRANSITION.NORMAL
    }
  },
  
  // ========================================================================
  // BUTTONS
  // ========================================================================
  BUTTON: {
    // Primary button
    PRIMARY: {
      BACKGROUND: COLORS.PRIMARY_BROWN,
      COLOR: COLORS.NEUTRAL_WHITE,
      BORDER: 'none',
      BORDER_RADIUS: LAYOUT.BORDER_RADIUS.LG,
      PADDING: '12px 32px',
      FONT_SIZE: TYPOGRAPHY.FONT_SIZE.LG,
      FONT_WEIGHT: TYPOGRAPHY.FONT_WEIGHT.SEMIBOLD,
      CURSOR: 'pointer',
      TRANSITION: LAYOUT.TRANSITION.NORMAL,
      
      HOVER: {
        BACKGROUND: COLORS.PRIMARY_BROWN_DARK
      },
      
      DISABLED: {
        BACKGROUND: COLORS.PRIMARY_BROWN_LIGHT,
        CURSOR: 'not-allowed'
      }
    },
    
    // Secondary button
    SECONDARY: {
      BACKGROUND: COLORS.NEUTRAL_WHITE,
      COLOR: COLORS.PRIMARY_BROWN,
      BORDER: `2px solid ${COLORS.PRIMARY_BROWN}`,
      BORDER_RADIUS: LAYOUT.BORDER_RADIUS.LG,
      PADDING: '12px 32px',
      FONT_SIZE: TYPOGRAPHY.FONT_SIZE.LG,
      FONT_WEIGHT: TYPOGRAPHY.FONT_WEIGHT.SEMIBOLD,
      CURSOR: 'pointer',
      TRANSITION: LAYOUT.TRANSITION.NORMAL,
      
      HOVER: {
        BACKGROUND: COLORS.PRIMARY_BROWN,
        COLOR: COLORS.NEUTRAL_WHITE
      }
    }
  },
  
  // ========================================================================
  // INPUTS
  // ========================================================================
  INPUT: {
    BORDER: `2px solid ${COLORS.NEUTRAL_GRAY}`,
    BORDER_RADIUS: LAYOUT.BORDER_RADIUS.LG,
    PADDING: '14px',
    FONT_SIZE: TYPOGRAPHY.FONT_SIZE.LG,
    FONT_WEIGHT: TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
    BACKGROUND: '#FAF9F7',
    COLOR: COLORS.NEUTRAL_TEXT,
    OUTLINE: 'none',
    TRANSITION: LAYOUT.TRANSITION.NORMAL,
    
    FOCUS: {
      BORDER_COLOR: COLORS.PRIMARY_BROWN,
      BOX_SHADOW: '0 0 0 3px rgba(201, 123, 83, 0.1)'
    },
    
    ERROR: {
      BORDER_COLOR: COLORS.ERROR_RED
    },
    
    SUCCESS: {
      BORDER_COLOR: COLORS.SUCCESS_GREEN
    }
  },
  
  // ========================================================================
  // CARDS
  // ========================================================================
  CARD: {
    BACKGROUND: COLORS.NEUTRAL_WHITE,
    BORDER_RADIUS: LAYOUT.BORDER_RADIUS.XL,
    SHADOW: LAYOUT.SHADOW.MD,
    PADDING: SPACING.XL,
    BORDER: `1px solid ${COLORS.NEUTRAL_GRAY}`,
    
    HOVER: {
      SHADOW: LAYOUT.SHADOW.LG,
      TRANSFORM: 'translateY(-2px)',
      TRANSITION: LAYOUT.TRANSITION.NORMAL
    }
  }
};

// ============================================================================
// GRADIENT UTILITIES
// ============================================================================

export const GRADIENT_UTILS = {
  // Get the primary gradient with animation
  getPrimaryGradient: () => ({
    background: COLORS.PRIMARY_GRADIENT,
    backgroundSize: LAYOUT.GRADIENT_ANIMATION.BACKGROUND_SIZE,
    animation: LAYOUT.GRADIENT_ANIMATION.ANIMATION
  }),
  
  // Get gradient text styling
  getGradientText: () => ({
    background: COLORS.PRIMARY_GRADIENT,
    backgroundSize: LAYOUT.GRADIENT_ANIMATION.BACKGROUND_SIZE,
    animation: LAYOUT.GRADIENT_ANIMATION.ANIMATION,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  }),
  
  // Get gradient button styling
  getGradientButton: () => ({
    background: COLORS.PRIMARY_GRADIENT,
    backgroundSize: LAYOUT.GRADIENT_ANIMATION.BACKGROUND_SIZE,
    animation: LAYOUT.GRADIENT_ANIMATION.ANIMATION,
    color: COLORS.NEUTRAL_WHITE,
    border: 'none',
    cursor: 'pointer',
    transition: LAYOUT.TRANSITION.NORMAL
  })
};

// ============================================================================
// USAGE EXAMPLES
// ============================================================================

export const USAGE_EXAMPLES = {
  // Back button examples
  BACK_BUTTON: {
    // Standard back button
    STANDARD: `
      <button
        onClick={handleBack}
        style={{
          background: COMPONENTS.BACK_BUTTON.BUTTON.BACKGROUND,
          border: COMPONENTS.BACK_BUTTON.BUTTON.BORDER,
          borderRadius: COMPONENTS.BACK_BUTTON.BUTTON.BORDER_RADIUS,
          padding: COMPONENTS.BACK_BUTTON.BUTTON.PADDING.MEDIUM,
          cursor: COMPONENTS.BACK_BUTTON.BUTTON.CURSOR,
          transition: COMPONENTS.BACK_BUTTON.BUTTON.TRANSITION
        }}
        aria-label="Go back"
      >
        <ChevronLeft 
          set="${COMPONENTS.BACK_BUTTON.ICON.SET}" 
          size={COMPONENTS.BACK_BUTTON.ICON.SIZE.MEDIUM} 
          primaryColor="${COMPONENTS.BACK_BUTTON.ICON.COLOR.PRIMARY}" 
        />
      </button>
    `,
    
    // Back link
    LINK: `
      <Link
        to="/previous-page"
        style={{
          display: COMPONENTS.BACK_BUTTON.LINK.DISPLAY,
          alignItems: COMPONENTS.BACK_BUTTON.LINK.ALIGN_ITEMS,
          gap: COMPONENTS.BACK_BUTTON.LINK.GAP,
          textDecoration: COMPONENTS.BACK_BUTTON.LINK.TEXT_DECORATION,
          transition: COMPONENTS.BACK_BUTTON.LINK.TRANSITION
        }}
      >
        <ChevronLeft 
          set="${COMPONENTS.BACK_BUTTON.ICON.SET}" 
          size={COMPONENTS.BACK_BUTTON.ICON.SIZE.SMALL} 
          primaryColor="${COMPONENTS.BACK_BUTTON.ICON.COLOR.LINK}" 
        />
        Back to Previous Page
      </Link>
    `
  }
};

// ============================================================================
// EXPORT ALL CONSTANTS
// ============================================================================

export default {
  COLORS,
  TYPOGRAPHY,
  SPACING,
  LAYOUT,
  COMPONENTS,
  GRADIENT_UTILS,
  USAGE_EXAMPLES
};
